'use client';

import React from "react";
import { Circle, CircleCheck, ChevronLeft } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config';

export default function Category({ catInfo, allCatSection }) {
  const pathname = usePathname() || '/';
  const currentBranchCode = getBranchCodeFromPath(pathname);
  const categoryInfo = catInfo;
  const allCategoryShop = allCatSection;

  const CategoryRow = ({
    href,
    label,
    isActive = false,
    leadingIcon = null,
    indentLevel = 0,
  }) => {
    const paddingClass = indentLevel > 0 ? 'pl-6 pr-3' : 'px-3';

    return (
    <a
      href={href}
      className={`flex items-center gap-2 rounded-lg ${paddingClass} py-2 text-sm cursor-pointer transition-colors text-gray-900 shadow-xs hover:bg-gray-100 ${
        isActive
          ? 'bg-primary/10 text-primary-600 font-medium'
          : 'text-default-700'
      }`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center ${
          leadingIcon ? 'text-default-400' : 'hidden'
        }`}
      >
        {leadingIcon ?? <Circle className="h-3.5 w-3.5" />}
      </span>
      <span className="flex-1">{label}</span>
      <span
        className={`flex h-5 w-5 items-center justify-center ${
          isActive ? 'text-primary' : 'text-default-300'
        }`}
      >
        {isActive ? (
          <CircleCheck className="h-4 w-4" />
        ) : (
          <Circle className="h-4 w-4" />
        )}
      </span>
    </a>
  );
  };

  const categories = Array.isArray(allCategoryShop) ? allCategoryShop : [];

  const breadcrumbs = Array.isArray(categoryInfo?.breadcrumbs)
    ? categoryInfo.breadcrumbs
    : [];

  const ancestors =
    breadcrumbs.length > 0
      ? breadcrumbs.slice(0, -1)
      : categoryInfo?.parentCode
        ? [
            {
              code: categoryInfo.parentCode,
              name: categoryInfo.parentName,
            },
          ]
        : [];

  // Проверяем, есть ли в списке дочерние разделы текущего узла
  const hasChildrenInList = categories.some(
    (cat) => cat.parentCode === categoryInfo?.code
  );
  const isLevelOne = categoryInfo?.depthLevel === '1';

  // Список, который отрисуем
  let categoriesToRender = categories;

  // Для раздела 1-го уровня с дочерними — добавляем родитель первым пунктом
  if (isLevelOne && categories.length > 0) {
    const alreadyHasParent = categories.some(
      (cat) => cat.code === categoryInfo?.code
    );
    if (!alreadyHasParent) {
      categoriesToRender = [categoryInfo, ...categories];
    }
  }

  // Для раздела с родителем и дочерними — добавляем текущий раздел над его детьми
  if (!isLevelOne && hasChildrenInList) {
    const hasSelf = categories.some(
      (cat) => cat.code === categoryInfo?.code
    );
    if (!hasSelf) {
      categoriesToRender = [categoryInfo, ...categoriesToRender];
    }
  }

  return (
    <div aria-label="Actions" className="flex flex-col gap-1">
      {ancestors.map((ancestor) => (
        <CategoryRow
          key={`ancestor-${ancestor.code}`}
          href={regionalizePath(
            `/shop/category/${ancestor.code}`,
            currentBranchCode
          )}
          label={ancestor.name}
          leadingIcon={<ChevronLeft className="h-4 w-4" />}
        />
      ))}

      {categoriesToRender.map((category) => {
        const isChildOfCurrent = category.parentCode === categoryInfo?.code;
        const indentLevel = isChildOfCurrent ? 1 : 0;
        return (
          <CategoryRow
            key={category.id ?? category.code}
            href={regionalizePath(
              `/shop/category/${category.code}`,
              currentBranchCode
            )}
            label={category.name}
            isActive={categoryInfo.code === category.code}
            indentLevel={indentLevel}
          />
        );
      })}
    </div>
  );
}
